// src/services/aiService.js - Service d'estimation IA avec fallback Groq → Mistral
import { calculateGL, getCategory } from '../utils/calculations';

/**
 * Service for AI-powered food estimation
 * Uses Groq API first, fallback to Mistral AI if Groq fails
 */
class AIService {
  constructor() {
    this.groqUrl = '/.netlify/functions/groq-proxy';
    this.mistralUrl = '/.netlify/functions/mistral-proxy';
    this.isAvailable = true;
  }

  isServiceAvailable() {
    return this.isAvailable;
  }

  getServiceStatus() {
    return {
      available: true,
      message: 'Service IA disponible (Mistral + Groq fallback)'
    };
  }

  createPrompt(foodName) {
    return `Agis comme un expert en nutrition et en diabétologie.

Ta mission: Estimer l'Indice Glycémique (IG) et les Glucides pour l'aliment suivant: "${foodName}"

RÈGLES STRICTES:
1. Réponds EXCLUSIVEMENT au format JSON valide
2. Ne fournis AUCUN texte avant ou après le JSON
3. Base-toi sur des données nutritionnelles scientifiques
4. Si l'aliment est inconnu ou ambigu, fournis une estimation prudente

FORMAT DE RÉPONSE OBLIGATOIRE:
{
  "name": "Nom exact de l'aliment en français",
  "ig": nombre entre 0 et 100,
  "carbs": nombre de glucides en g/100g (peut être 0),
  "confidence": "faible" ou "moyenne" ou "élevée",
  "explanation": "Explication courte (1-2 phrases) de l'estimation"
}

EXEMPLES DE VALEURS:
- Huile, Viande, Rhum pur: IG 0, glucides 0g
- Légumes verts: IG 15-25, glucides 2-5g
- Légumineuses: IG 25-35, glucides 15-20g
- Fruits: IG 35-50, glucides 10-15g
- Pain blanc: IG 70-75, glucides 50g
- Riz blanc: IG 70, glucides 28g
- Sucre: IG 68, glucides 100g

Aliment à estimer: "${foodName}"

Réponds uniquement avec le JSON, sans aucun autre texte.`;
  }

  createRecipePrompt(dishName, optimizeCG = false) {
    const cgSection = optimizeCG
      ? `
8. Le champ "cg_data" DOIT inclure un tableau "health_tips" avec 2-3 conseils concrets pour réduire la charge glycémique, et un tableau "alternative_ingredients" avec des substituts IG plus bas pour les ingrédients principaux.`
      : `
8. Les champs "health_tips" et "alternative_ingredients" dans "cg_data" peuvent être des tableaux vides.`;

    return `Tu es un chef cuisinier expert en nutrition à faible indice glycémique.

Ta mission : Génère une recette classique à IG bas pour : "${dishName}"

Structure le JSON pour que je puisse facilement ajouter des instructions techniques pour robots culinaires plus tard.

RÈGLES STRICTES :
1. Réponds EXCLUSIVEMENT au format JSON valide
2. La recette doit être saine, savoureuse et à IG bas (< 55)
3. Les instructions doivent être claires et détaillées
4. Estime honnêtement les valeurs glycémiques
5. Les ingrédients doivent être précis (nom + quantité + unité)
6. "appliance_configs" doit être un objet VIDE {} (extensions futures pour robots)
7. "cg_data" doit contenir les métriques glycémiques estimées${cgSection}

FORMAT OBLIGATOIRE :
{
  "title": "Titre de la recette",
  "ingredients": [
    { "name": "Nom ingrédient", "quantity": 200, "unit": "g", "gi_note": "IG bas – fibre élevée" }
  ],
  "base_instructions": "Instructions complètes étape par étape en français",
  "appliance_configs": {},
  "cg_data": {
    "estimated_gi": 35,
    "estimated_gl_per_serving": 12,
    "servings": 4,
    "health_tips": [],
    "alternative_ingredients": []
  }
}

Réponds uniquement avec le JSON, sans aucun autre texte.`;
  }

  createGroqRequest(foodName) {
    return {
      model: "openai/gpt-oss-120b",
      messages: [
        {
          role: "user",
          content: this.createPrompt(foodName)
        }
      ],
      temperature: 0.2,
      response_format: { type: "json_object" }
    };
  }

  createMistralRequest(foodName) {
    return {
      model: "mistral-small-latest",
      messages: [
        {
          role: "user",
          content: this.createPrompt(foodName)
        }
      ],
      temperature: 0.2,
      response_format: { type: "json_object" }
    };
  }

  parseResponse(responseText) {
    let cleanedText = responseText.trim();
    cleanedText = cleanedText.replace(/```json\n?/g, '');
    cleanedText = cleanedText.replace(/```\n?/g, '');
    cleanedText = cleanedText.trim();

    let parsed;
    try {
      parsed = JSON.parse(cleanedText);
    } catch (error) {
      throw new Error('La réponse de l\'IA n\'est pas au format JSON valide');
    }

    if (!parsed.name || typeof parsed.name !== 'string') {
      throw new Error('Nom de l\'aliment manquant ou invalide');
    }

    if (typeof parsed.ig !== 'number' || parsed.ig < 0 || parsed.ig > 100) {
      throw new Error('Indice Glycémique invalide (doit être entre 0 et 100)');
    }

    if (typeof parsed.carbs !== 'number' || parsed.carbs < 0) {
      throw new Error('Glucides invalides (doit être >= 0)');
    }

    if (!parsed.explanation || typeof parsed.explanation !== 'string') {
      throw new Error('Explication manquante');
    }

    const validConfidence = ['faible', 'moyenne', 'élevée'];
    if (!parsed.confidence || !validConfidence.includes(parsed.confidence)) {
      parsed.confidence = 'faible';
    }

    return parsed;
  }

  async callAPI(url, requestBody, provider) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${provider} API error ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (!data.choices || data.choices.length === 0 || !data.choices[0].message) {
        throw new Error(`Aucune réponse de ${provider}`);
      }

      return data.choices[0].message.content;
    } catch (error) {
      throw new Error(`Erreur ${provider}: ${error.message}`);
    }
  }

  async estimateFood(foodName) {
    if (!foodName || typeof foodName !== 'string' || foodName.trim().length === 0) {
      throw new Error('Nom d\'aliment invalide');
    }

    const cleanFoodName = foodName.trim();
    let aiEstimate;
    let provider;
    let errors = [];

    // Tentative 1: Mistral (primaire)
    try {
      console.log('🚀 Tentative avec Mistral...');
      const mistralRequest = this.createMistralRequest(cleanFoodName);
      const responseText = await this.callAPI(this.mistralUrl, mistralRequest, 'Mistral');
      aiEstimate = this.parseResponse(responseText);
      provider = 'Mistral AI';
      console.log('✅ Mistral a répondu avec succès');
    } catch (mistralError) {
      errors.push(`Mistral: ${mistralError.message}`);
      console.warn('⚠️ Mistral a échoué, fallback vers Groq...', mistralError.message);

      // Tentative 2: Groq (fallback)
      try {
        const groqRequest = this.createGroqRequest(cleanFoodName);
        const responseText = await this.callAPI(this.groqUrl, groqRequest, 'Groq');
        aiEstimate = this.parseResponse(responseText);
        provider = 'Groq (GPT-OSS 120B)';
        console.log('✅ Groq a répondu avec succès (fallback)');
      } catch (groqError) {
        errors.push(`Groq: ${groqError.message}`);
        console.error('❌ Groq a aussi échoué');
        throw new Error(`Les deux services IA ont échoué:\n${errors.join('\n')}`);
      }
    }

    const gl = calculateGL(aiEstimate.ig, aiEstimate.carbs, 100);
    const category = getCategory(gl);

    return {
      id: Date.now(),
      name: aiEstimate.name,
      gi: Math.round(aiEstimate.ig),
      carbs: Math.round(aiEstimate.carbs * 10) / 10,
      gl: gl,
      category: category,
      source: 'ai',
      confidence: aiEstimate.confidence,
      explanation: aiEstimate.explanation,
      estimatedBy: provider,
      estimatedAt: new Date().toISOString(),
      warning: 'Estimation IA - Vérifiez avec des sources fiables pour un usage médical'
    };
  }

  async generateRecipe(dishName, optimizeCG = false) {
    if (!dishName || typeof dishName !== 'string' || dishName.trim().length === 0) {
      throw new Error('Nom de plat invalide');
    }

    const prompt = this.createRecipePrompt(dishName.trim(), optimizeCG);
    const body = {
      model: 'mistral-small-latest',
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.3,
      response_format: { type: 'json_object' }
    };

    let responseText;
    let provider;

    try {
      responseText = await this.callAPI(this.mistralUrl, body, 'Mistral');
      provider = 'Mistral AI';
    } catch (mistralErr) {
      console.warn('Mistral recipe failed, fallback Groq:', mistralErr.message);
      const groqBody = { ...body, model: 'openai/gpt-oss-120b' };
      responseText = await this.callAPI(this.groqUrl, groqBody, 'Groq');
      provider = 'Groq (GPT-OSS 120B)';
    }

    let cleaned = responseText.trim().replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const recipe = JSON.parse(cleaned);

    if (!recipe.title || !Array.isArray(recipe.ingredients) || !recipe.base_instructions) {
      throw new Error('Structure de recette invalide renvoyée par l\'IA');
    }

    return {
      ...recipe,
      appliance_configs: recipe.appliance_configs || {},
      cg_data: recipe.cg_data || {},
      generatedBy: provider,
      generatedAt: new Date().toISOString()
    };
  }

  async estimateFoodsBatch(foodNames, delayMs = 1000) {
    if (!Array.isArray(foodNames) || foodNames.length === 0) {
      throw new Error('Liste d\'aliments invalide');
    }

    const results = [];
    const errors = [];

    for (let i = 0; i < foodNames.length; i++) {
      try {
        const result = await this.estimateFood(foodNames[i]);
        results.push(result);

        if (i < foodNames.length - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      } catch (error) {
        errors.push({
          foodName: foodNames[i],
          error: error.message
        });
      }
    }

    return {
      success: results,
      failed: errors,
      total: foodNames.length,
      successCount: results.length,
      failedCount: errors.length
    };
  }

  validateEstimation(estimation) {
    const warnings = [];

    if (estimation.carbs === 0 && estimation.gi > 15) {
      warnings.push('⚠️ Aliment sans glucides ne devrait pas avoir un IG élevé');
    }

    if (estimation.carbs > 50 && estimation.gi < 40) {
      warnings.push('⚠️ Combinaison inhabituelle: beaucoup de glucides avec IG bas');
    }

    if (estimation.carbs < 5 && estimation.gl > 5) {
      warnings.push('⚠️ CG élevée inhabituelle pour peu de glucides');
    }

    return {
      isValid: warnings.length === 0,
      warnings: warnings,
      confidence: warnings.length > 0 ? 'faible' : estimation.confidence
    };
  }
}

export const aiService = new AIService();
export default aiService;
